import { cn } from '@workspace/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import React, { forwardRef } from 'react';
import { RenderedContent } from '../../utils';

const inputVariants = cva(
  // Classes de base
  [
    'h-10 w-full rounded-md px-4 text-sm transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-0',
    'placeholder:text-input-text-placeholder',
    'disabled:cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-input-bg border border-input-border text-input-text',
          'hover:border-input-border-hover',
          'focus:border-input-border-focus focus:bg-input-bg-focus focus:ring-primary/20',
          'disabled:bg-input-bg-disabled disabled:border-input-border-disabled disabled:text-input-text-disabled',
        ],
        error: [
          'bg-input-bg-error border border-input-border-error text-input-text',
          'focus:border-input-border-error focus:bg-input-bg-error focus:ring-error/20',
          'disabled:bg-input-bg-disabled disabled:border-input-border-disabled disabled:text-input-text-disabled',
        ],
        success: [
          'bg-input-bg-success border border-input-border-success text-input-text',
          'focus:border-input-border-success focus:bg-input-bg-success focus:ring-success/20',
          'disabled:bg-input-bg-disabled disabled:border-input-border-disabled disabled:text-input-text-disabled',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const loaderVariants = cva(
  'absolute right-3 top-1/2 -translate-y-1/2 animate-spin rounded-full border-2 border-r-transparent h-4 w-4',
  {
    variants: {
      variant: {
        default: 'border-primary',
        error: 'border-error',
        success: 'border-success',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'placeholder'>,
    VariantProps<typeof inputVariants> {
  label?: RenderedContent;
  children?: React.ReactNode;
  placeholder?: RenderedContent;
  type?: 'text' | 'email' | 'number' | 'tel' | 'date' | 'datetime';
  className?: string;
  loading?: boolean;
  error?: boolean;
  success?: boolean;
  disabled?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({
     type = 'text',
     variant,
     className,
     loading,
     error,
     success,
     disabled,
    placeholder,
     ...props
   }, ref) => {
    // Logique pour déterminer la variante
    const computedVariant = error ? 'error' : success ? 'success' : variant || 'default';

    // Prevent the input value change on wheel event
    const numberInputOnWheelPreventChange = (e: React.WheelEvent<HTMLInputElement>) => {
      e.currentTarget.blur();
      e.stopPropagation();
      setTimeout(() => {
        e.currentTarget.focus();
      }, 0);
    };

    const inputMode = (type: string): React.HTMLAttributes<HTMLInputElement>['inputMode'] => {
      switch (type) {
        case 'number':
          return 'numeric';
        case 'tel':
          return 'tel';
        case 'email':
          return 'email';
        case 'date':
          return 'numeric';
        default:
          return undefined;
      }
    };

    return (
      <div className="relative">
        <input
          placeholder={placeholder ? String(placeholder) : undefined}
          className={cn(inputVariants({ variant: computedVariant }), className)}
          inputMode={inputMode(type)}
          onWheel={type === 'number' ? numberInputOnWheelPreventChange : undefined}
          ref={ref}
          type={type}
          disabled={disabled}
          {...props}
        />
        {loading && (
          <span className={loaderVariants({ variant: computedVariant })} />
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
